import React, { useState } from 'react';
import { Box, Button, Alert, CircularProgress, Typography, Paper } from '@mui/material';
import { Refresh as RefreshIcon, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { esocialService } from '../../../../services/esocialService';

interface Props {
  companyId: string;
  eventId: string;
}

const XmlPreviewPanel: React.FC<Props> = ({ companyId, eventId }) => {
  const [xml, setXml] = useState<string | null>(null);
  const [validation, setValidation] = useState<{ valid: boolean; errors?: Array<{ message: string }> } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await esocialService.previewXml(companyId, eventId);
      setXml(result.xml);
      setValidation(result.validation);
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(apiErr.response?.data?.error || apiErr.message || 'Erro ao gerar XML');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1">Pre-visualizacao XML</Typography>
        <Button
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={loadPreview}
          disabled={loading}
          size="small"
        >
          {xml ? 'Atualizar' : 'Gerar XML'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {validation && (
        <Alert
          severity={validation.valid ? 'success' : 'warning'}
          sx={{ mb: 2 }}
          icon={validation.valid ? <CheckCircle /> : <ErrorIcon />}
        >
          {validation.valid
            ? 'XML valido'
            : `${validation.errors?.length ?? 0} erro(s) de validacao`}
          {validation.errors?.map((e, i) => (
            <Typography key={i} variant="body2">- {e.message}</Typography>
          ))}
        </Alert>
      )}

      {xml && (
        <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
          <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{xml}</pre>
        </Paper>
      )}
    </Box>
  );
};

export default XmlPreviewPanel;
